import { FC, Suspense, useEffect, useState } from 'react';
import DashboardHeader from '../components/Dashboard/DashboardHeader';
import DashboardList from '../components/Dashboard/DashboardList';
import { StudentSkillStates } from '../models/StudentSkillState';
import { getDiagnosticInformation, getStudentExitSlip } from '../graphQLOperations/Dashboard';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { Diagnostic } from '../components/DiagnosticUI/Diagnostic';
import { MainFallback } from '../components/Fallback/MainFallBack';
import { PreloadedQuery, useLazyLoadQuery, useQueryLoader } from 'react-relay';
import { selectedTargetGroup, setTargetGroup } from '../redux/reducers/targetGroupSlice';
import { DashboardDiagnosticQuery } from '../graphQLOperations/__generated__/DashboardDiagnosticQuery.graphql';
import { splitId } from '../businessLogicService/splitIdsFromBackEnd';
import { selectedAccessToken } from '../redux/reducers/accessTokenSlice';
import { useAuth0 } from '@auth0/auth0-react';
import SkillStateUpdateLoader from '../components/SkillStateUpdateLoader/SkillStateUpdateLoader';
import IsInferredQueryComponent from '../components/Dashboard/IsInferredQueryComponent';
import { DashboardGetStudentExitSlipQuery } from '../graphQLOperations/__generated__/DashboardGetStudentExitSlipQuery.graphql';
import { CustomBranding, mapCustomBranding } from '../models/User';
import { endOfDay, format, startOfDay } from 'date-fns';
import { isTargetGroupInSite } from '../businessLogicService/isTargetGroupInSite';
import { DashboardNoRoadmapSelectedModal } from '../components/Dashboard/DashboardNoRoadmapSelectedModal';
import { getDateByUserTimezone } from '../businessLogicService/getDateByUserTimezone';
import { TargetGroup } from '../models/TargetGroup';
import WeeklyGoalQueryWrapper from '../components/WeeklyGoal/WeeklyGoalQueryWrapper';
import { DiagnosticStars } from '../components/Dashboard/DiagnosticStars';
import { StudentFocusController } from '../components/UnitFocus/StudentFocusController';
import CustomErrorBoundary from '../components/LayoutWrapper/CustomErrorBoundary';

interface Props {
  isInferred: boolean;
  setIsInferred: (i: boolean) => void;
}

const DashboardStudent: FC<Props> = ({ isInferred, setIsInferred }) => {
  const { isAuthenticated } = useAuth0();
  const dispatch = useAppDispatch();
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const userRedux = useAppSelector(selectUser);
  const accessTokenRedux = useAppSelector(selectedAccessToken);
  const [previousDiagnosticId, setPreviousDiagnosticId] = useState<string | null>(null);
  const [studentSkillStates, setStudentSkillStates] = useState<StudentSkillStates[]>();
  const [sssWithoutExtension, setSSSWithoutExtension] = useState<StudentSkillStates[]>();
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [waitToRender, setWaitToRender] = useState(true);
  const [firstRender, setFirstRender] = useState(true);
  const [showInferredSkills, setShowInferredSkills] = useState(false);

  const { latestCompletedStudentSkillDiagnostic, currentTargetGroupByStudentId } =
    useLazyLoadQuery<DashboardDiagnosticQuery>(
      getDiagnosticInformation,
      {
        skip: !isAuthenticated || !accessTokenRedux,
        studentId: `StudentNode:${userRedux?.id}`,
      },
      { fetchKey: accessTokenRedux || '' },
    );

  const [queryReferenceExitSlip, loadExitSlipQuery] =
    useQueryLoader<DashboardGetStudentExitSlipQuery>(getStudentExitSlip);

  const currentTargets = (currentTargetGroupByStudentId?.targetGroup?.targets ?? []).filter(
    (v): v is NonNullable<typeof v> => v != null,
  );

  const targetGroupsNodes =
    userRedux?.sites
      ?.map(
        site =>
          site?.targetGroups?.map(target => ({
            id: target?.id || '',
            name: target?.name || '',
          })) ?? [],
      )
      ?.flat() ?? [];

  const dashboardContent = (
    <>
      {targetGroupRedux && (
        <DashboardHeader
          allTargetsGroup={targetGroupsNodes as TargetGroup[]}
          currentTargets={currentTargets}
          setStudentSkillStates={setStudentSkillStates}
          setSSSWithoutExtension={setSSSWithoutExtension}
        />
      )}

      {studentSkillStates &&
        sssWithoutExtension &&
        targetGroupRedux?.id &&
        isTargetGroupInSite(userRedux, targetGroupRedux) && (
          <Suspense fallback={<MainFallback />}>
            <DashboardList
              studentSkillStates={studentSkillStates}
              firstRender={firstRender}
              setFirstRender={setFirstRender}
              queryReferenceExitSlip={queryReferenceExitSlip as PreloadedQuery<DashboardGetStudentExitSlipQuery>}
            />
          </Suspense>
        )}
    </>
  );

  useEffect(() => {
    if (userRedux && mapCustomBranding[userRedux?.network?.customBranding ?? ''] === CustomBranding.MIAMI) {
      loadExitSlipQuery({
        filter: {
          userIds: [`UserNode:${userRedux?.id as string}`],
          dueOnFrom: format(getDateByUserTimezone(userRedux?.activeTimeZone || '').queryDate, 'yyyy-MM-dd'),
          dueOnTo: format(getDateByUserTimezone(userRedux?.activeTimeZone || '').queryDate, 'yyyy-MM-dd'),
        },
        learnosityExitSlipSessionsFilter: {
          userIds: [`UserNode:${userRedux?.id as string}`],
          sessionCompletedOn: format(
            endOfDay(getDateByUserTimezone(userRedux?.activeTimeZone || '').queryDate),
            'yyyy-MM-dd HH:mm:ss',
          ),
          sessionStartedOn: format(
            startOfDay(getDateByUserTimezone(userRedux?.activeTimeZone || '').queryDate),
            'yyyy-MM-dd HH:mm:ss',
          ),
        },
      });
    }
  }, [userRedux]);

  useEffect(() => {
    if (currentTargetGroupByStudentId) {
      dispatch(setTargetGroup(currentTargetGroupByStudentId.targetGroup as TargetGroup));
    }
  }, []);

  useEffect(() => {
    const diagnosticFinished = localStorage.getItem('diagnostic_finished');
    if (diagnosticFinished) {
      if (diagnosticFinished !== 'true') setShowDiagnostic(true);
    } else {
      if (!latestCompletedStudentSkillDiagnostic || latestCompletedStudentSkillDiagnostic?.retakeAssigned) {
        setPreviousDiagnosticId(latestCompletedStudentSkillDiagnostic?.id || '');
        setShowDiagnostic(true);
      }
    }

    if (latestCompletedStudentSkillDiagnostic?.isInferred && !latestCompletedStudentSkillDiagnostic.retakeAssigned) {
      setIsInferred(true);
      localStorage.removeItem('diagnostic_finished');
    }
    setWaitToRender(false);
  }, [latestCompletedStudentSkillDiagnostic]);

  useEffect(() => {
    const tgName = targetGroupRedux?.name?.trim();
    document.title = tgName ? `Teach to One Roadmaps | Dashboard | ${tgName}` : 'Teach to One Roadmaps | Dashboard';
  }, [targetGroupRedux?.name]);

  return (
    <>
      <div className="grid nc_content-frame w-full">
        {!waitToRender && (
          <>
            {/* Must match shared/Nc.Shared/Learnosity/LearnosityActivityHelper.cs. */}
            {showDiagnostic && (
              <Diagnostic
                latestCompletedDiagnostic={latestCompletedStudentSkillDiagnostic || null}
                setShowDiagnostic={setShowDiagnostic}
                activityId={`Diagnostic_${splitId(userRedux?.gradeLevel?.id as string)}_V2.1`}
              />
            )}
            {!showDiagnostic && isTargetGroupInSite(userRedux, targetGroupRedux) && (
              <Suspense fallback={<MainFallback />}>
                {isInferred ? (
                  !showInferredSkills ? (
                    <WeeklyGoalQueryWrapper>{dashboardContent}</WeeklyGoalQueryWrapper>
                  ) : (
                    <Suspense>
                      <DiagnosticStars setShowInferredSkills={setShowInferredSkills} />
                    </Suspense>
                  )
                ) : (
                  <SkillStateUpdateLoader />
                )}
              </Suspense>
            )}
          </>
        )}
      </div>

      {/* Only announce the focus once the student has landed on the dashboard — not while the
          roadmap-building loader (!isInferred) or the "roadmap ready" stars (showInferredSkills)
          are on screen, since NewFocusModal is a locked, full-screen dialog (TEAMC-3176). */}
      {!waitToRender && !showDiagnostic && isAuthenticated && accessTokenRedux && isInferred && !showInferredSkills && (
        <Suspense fallback={null}>
          <StudentFocusController currentTargets={currentTargets} fetchKey={accessTokenRedux || ''} />
        </Suspense>
      )}
      {!waitToRender && !isTargetGroupInSite(userRedux, targetGroupRedux) && !showDiagnostic && (
        <DashboardNoRoadmapSelectedModal />
      )}
      {!waitToRender && isAuthenticated && accessTokenRedux && !showDiagnostic && !isInferred && (
        <Suspense>
          <CustomErrorBoundary fallback={<></>} onError={() => setIsInferred(false)}>
            <IsInferredQueryComponent
              setShowInferredSkills={setShowInferredSkills}
              previousDiagnosticId={previousDiagnosticId}
              setIsInferred={setIsInferred}
            />
          </CustomErrorBoundary>
        </Suspense>
      )}
    </>
  );
};

export default DashboardStudent;
